import { useState, useEffect, useRef, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'
import { useAppStore } from '../../store/appStore'

interface CallFrame { name: string; file: string; line: number }
interface Variable { name: string; value: string; type: string }

export function DebuggerPanel() {
  const [active, setActive] = useState(false)
  const [paused, setPaused] = useState(false)
  const [callFrames, setCallFrames] = useState<CallFrame[]>([])
  const [variables, setVariables] = useState<Variable[]>([])
  const [output, setOutput] = useState<string[]>([])
  const [starting, setStarting] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const activeTab = useAppStore(s => s.openTabs.find(t => t.id === s.activeTabId))

  const pollState = useCallback(async () => {
    try {
      const res = await authFetch(apiUrl('/api/debug/state'))
      const data = await res.json()
      setActive(data.active ?? false)
      setPaused(data.paused ?? false)
      setCallFrames(data.callFrames ?? [])
      setVariables(data.variables ?? [])
      if (data.active && !data.connected) {
        setOutput(prev => {
          if (prev.includes('⏳ Connecting to debugger...')) return prev
          return [...prev, '⏳ Connecting to debugger...']
        })
      }
      if (data.active && data.connected && data.paused) {
        setOutput(prev => {
          if (prev.includes('⏸ Paused at breakpoint')) return prev
          return [...prev, '⏸ Paused at breakpoint']
        })
      }
      if (!data.active && active) {
        setOutput(prev => [...prev, '⏹ Process exited'])
        setActive(false)
      }
    } catch {}
  }, [active])

  useEffect(() => {
    if (active) {
      pollRef.current = setInterval(pollState, 1500)
      return () => { if (pollRef.current) clearInterval(pollRef.current) }
    } else {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    }
  }, [active, pollState])

  const start = async () => {
    if (!activeTab) return
    const ext = activeTab.language
    if (!['js', 'ts', 'tsx', 'jsx', 'py'].includes(ext)) {
      setOutput(prev => [...prev, `✗ Cannot debug .${ext} files. Open a .js, .ts, or .py file.`])
      return
    }
    setStarting(true)
    setOutput([])
    try {
      const file = activeTab.path.replace(/^\//, '')
      const res = await authFetch(apiUrl('/api/debug/start'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file, language: activeTab.language }),
      })
      const data = await res.json()
      if (data.ok) { setActive(true); setOutput(prev => [...prev, `▶ Debugging ${file} (${data.language})`]) }
      else setOutput(prev => [...prev, `✗ ${data.error}`])
    } catch (e: any) { setOutput(prev => [...prev, `✗ ${e.message}`]) }
    finally { setStarting(false) }
  }

  const stop = async () => {
    await authFetch(apiUrl('/api/debug/stop'), { method: 'POST' }).catch(() => {})
    setActive(false); setPaused(false); setCallFrames([]); setVariables([])
    setOutput(prev => [...prev, '⏹ Session ended'])
  }

  const action = async (cmd: string) => {
    await authFetch(apiUrl(`/api/debug/${cmd}`), { method: 'POST' }).catch(() => {})
    setTimeout(pollState, 200)
  }

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <span>DEBUGGER</span>
        <span style={{ fontSize: 9, color: active ? 'var(--accent-2)' : 'var(--text-2)' }}>
          {active ? (paused ? '⏸ PAUSED' : '▶ RUNNING') : '○ IDLE'}
        </span>
      </div>

      {/* Controls */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 4, flexShrink: 0 }}>
        {!active ? (
          <button onClick={start} disabled={starting || !activeTab} style={{ ...btnStyle, borderColor: 'var(--accent-2)', color: 'var(--accent-2)' }}>
            {starting ? 'Starting...' : '▶ Debug'}
          </button>
        ) : (
          <>
            <button onClick={() => action('continue')} disabled={!paused} style={btnStyle} title="Continue (F5)">▶</button>
            <button onClick={() => action('step-over')} disabled={!paused} style={btnStyle} title="Step Over (F10)">⤵</button>
            <button onClick={() => action('step-into')} disabled={!paused} style={btnStyle} title="Step Into (F11)">↓</button>
            <button onClick={() => action('step-out')} disabled={!paused} style={btnStyle} title="Step Out (Shift+F11)">↑</button>
            <div style={{ flex: 1 }} />
            <button onClick={stop} style={{ ...btnStyle, borderColor: 'var(--red)', color: 'var(--red)' }}>⏹ Stop</button>
          </>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
        {/* Variables */}
        {variables.length > 0 && (
          <div style={{ borderBottom: '1px solid var(--border)' }}>
            <div style={sectionHeader}>VARIABLES</div>
            {variables.map((v, i) => (
              <div key={i} style={{ padding: '3px 12px', display: 'flex', gap: 8 }}>
                <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{v.name}</span>
                <span style={{ color: 'var(--text-2)' }}>=</span>
                <span style={{ color: typeColor(v.type), flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.value}</span>
                <span style={{ color: 'var(--text-2)', fontSize: 9 }}>{v.type}</span>
              </div>
            ))}
          </div>
        )}

        {/* Call Stack */}
        {callFrames.length > 0 && (
          <div style={{ borderBottom: '1px solid var(--border)' }}>
            <div style={sectionHeader}>CALL STACK</div>
            {callFrames.map((f, i) => (
              <div key={i} style={{ padding: '3px 12px', display: 'flex', gap: 8, color: i === 0 ? 'var(--text-0)' : 'var(--text-2)' }}>
                <span style={{ fontWeight: i === 0 ? 700 : 400 }}>{f.name}</span>
                <span style={{ marginLeft: 'auto', fontSize: 9 }}>{f.file}:{f.line}</span>
              </div>
            ))}
          </div>
        )}

        {/* Output */}
        <div>
          <div style={sectionHeader}>OUTPUT</div>
          <div style={{ padding: '4px 12px', color: 'var(--text-1)', lineHeight: 1.6 }}>
            {output.length === 0 && !active && (
              <div style={{ color: 'var(--text-2)', padding: '8px 0' }}>
                Open a .js/.ts/.py file and click ▶ Debug
              </div>
            )}
            {output.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        </div>
      </div>

      {/* Info */}
      {!active && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)', fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
          Supports: Node.js (--inspect) · Python (debugpy)
        </div>
      )}
    </div>
  )
}

function typeColor(type: string): string {
  switch (type) {
    case 'string': return '#ce9178'
    case 'number': return '#b5cea8'
    case 'boolean': return '#569cd6'
    case 'object': return 'var(--accent)'
    case 'function': return 'var(--accent-3)'
    default: return 'var(--text-1)'
  }
}

const panelStyle: React.CSSProperties = { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }
const headerStyle: React.CSSProperties = { padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const btnStyle: React.CSSProperties = { padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border-2)', background: 'none', color: 'var(--text-1)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700 }
const sectionHeader: React.CSSProperties = { padding: '6px 12px', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-2)', textTransform: 'uppercase', background: 'var(--bg-2)' }
